import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useSWR from 'swr';
import { regulatoryService } from 'services';
import { useAuth } from 'hooks';
import { KycStatus } from 'types';
import { Button, Heading, Loading, Text } from 'components';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from 'helpers';
import {
  FontSize,
  FontWeight,
  LocalStorageKeys,
  ONE_MINUTE_MILLISECONDS,
  routes,
} from 'commons';
import * as S from './styles';

interface Props {
  kycStatus: KycStatus;
}

const TWENTY_MINUTES_MILLISECONDS = 20 * ONE_MINUTE_MILLISECONDS;

function deleteExpiredStoredTimers() {
  const storedTimers = getLocalStorageItem(
    LocalStorageKeys.RegulatoryEmailSendTime
  );

  if (!storedTimers) return;

  Object.entries(storedTimers).forEach(([key, value]) => {
    if (typeof value === 'number' && value <= Date.now()) {
      delete storedTimers[key];
    }
  });

  const isObjectEmpty = Object.keys(storedTimers).length === 0;

  if (isObjectEmpty) {
    removeLocalStorageItem(LocalStorageKeys.RegulatoryEmailSendTime);
  } else {
    setLocalStorageItem(LocalStorageKeys.RegulatoryEmailSendTime, storedTimers);
  }
}

function getStoreStoredTimer(store: string | null) {
  if (!store) return null;

  const storedTimers = getLocalStorageItem(
    LocalStorageKeys.RegulatoryEmailSendTime,
    {}
  );

  return storedTimers[store];
}

export function RegulatoryInformation({ kycStatus }: Props) {
  const { selectedStore } = useAuth();
  const { name: store } = selectedStore;

  const isKycIncompleted = kycStatus === KycStatus.IncompletedKyc;

  const { data, isLoading } = useSWR(
    isKycIncompleted ? `regulatory-${store}` : null,
    () => regulatoryService.getForm({ store }),
    { revalidateOnFocus: false }
  );

  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const savedDate = getStoreStoredTimer(store);

    if (savedDate) {
      const now = Date.now();

      if (savedDate > now) {
        setIsValidating(true);

        const timer = setTimeout(() => {
          setIsValidating(false);
          deleteExpiredStoredTimers();
        }, savedDate - now);

        return () => {
          clearTimeout(timer);
        };
      }
    } else {
      setIsSendingEmail(false);
      setIsValidating(false);
    }

    deleteExpiredStoredTimers();
  }, [store]);

  function navigateToForm() {
    navigate(routes.regulatoryInformation.path);
  }

  if (kycStatus === KycStatus.WithoutVendorRegisterInformation) {
    return (
      <S.Main>
        <S.Wrapper>
          <Heading as="h5" size={FontSize.font16} weight={FontWeight.bold}>
            Seus dados estão desatualizados.
          </Heading>

          <Text size={FontSize.font12}>
            É necessário que o Sócio/Representante legal atualize-os em duas
            etapas para regularizar o recebimento de valores junto ao
            adquirente, de acordo com a Normativa do BACEN{' '}
            <a
              className="bold"
              href="https://www.bcb.gov.br/estabilidadefinanceira/exibenormativo?tipo=Circular&numero=3978"
              target="_blank"
              rel="noreferrer"
            >
              circular 3.978/20
            </a>
          </Text>

          <Text className="danger" size={FontSize.font10}>
            *Estas informações são necessárias para manter a agenda de
            recebíveis da loja ativa.
          </Text>
        </S.Wrapper>

        <Button onClick={navigateToForm} full>
          Atualizar dados
        </Button>
      </S.Main>
    );
  }

  async function sendEmail() {
    setIsSendingEmail(true);

    if (store === null) {
      setIsSendingEmail(false);
      return toast.warn(`A loja precisa ser informada`);
    }

    const result = await regulatoryService.postUpdateKyc({ store });

    if (result.status === 200) {
      setIsValidating(true);

      const storedTimers = getLocalStorageItem(
        LocalStorageKeys.RegulatoryEmailSendTime,
        {}
      );

      const newTimers = {
        [store]: Date.now() + TWENTY_MINUTES_MILLISECONDS,
        ...storedTimers,
      };

      setLocalStorageItem(LocalStorageKeys.RegulatoryEmailSendTime, newTimers);

      setTimeout(() => {
        setIsValidating(false);
        deleteExpiredStoredTimers();
      }, TWENTY_MINUTES_MILLISECONDS);
    }

    setIsSendingEmail(false);
  }

  if (kycStatus === KycStatus.IncompletedKyc) {
    if (isLoading) {
      return (
        <S.Main>
          <Loading />
        </S.Main>
      );
    }

    if (!data) {
      return (
        <S.Main>
          <Text size={FontSize.font12}>Erro ao carregar o formulário</Text>
        </S.Main>
      );
    }

    const {
      managingPartner: { email },
    } = data;

    return (
      <S.Main>
        <S.Wrapper>
          <Heading as="h5" size={FontSize.font16} weight={FontWeight.bold}>
            Quase lá!
          </Heading>

          <Text size={FontSize.font12}>
            O formulário já foi preenchido. O sócio/representante receberá um
            email em {email} para realizar a Validação de Identidade.
          </Text>

          <Text size={FontSize.font12}>
            Lembrete: O sócio terá 20 minutos para responder após a chegada do
            email.
          </Text>

          <Text className="danger" size={FontSize.font10}>
            *Estas informações são necessárias para manter a agenda de
            recebíveis da loja ativa.
          </Text>
        </S.Wrapper>

        <Button
          onClick={sendEmail}
          full
          isLoading={isSendingEmail}
          disabled={isValidating}
        >
          {isValidating ? 'Validando dados' : 'Enviar e-mail'}
        </Button>
      </S.Main>
    );
  }

  return null;
}
